import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const GOLD = '#FFC220';

const emptyForm = {
  plan_name: '',
  illumia_plan_id: '',
  plan_type: 'fmp',
  active: true,
};

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('plans')
      .select('*')
      .order('plan_type', { ascending: true })
      .order('plan_name', { ascending: true });

    if (!error) setPlans(data);
    setLoading(false);
  };

  const handleEdit = (plan) => {
    setForm({
      plan_name: plan.plan_name || '',
      illumia_plan_id: plan.illumia_plan_id || '',
      plan_type: plan.plan_type || 'fmp',
      active: plan.active,
    });
    setEditingId(plan.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    await supabase.from('plans').delete().eq('id', id);
    fetchPlans();
  };

  const handleToggle = async (plan) => {
    await supabase
      .from('plans')
      .update({ active: !plan.active })
      .eq('id', plan.id);
    fetchPlans();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.plan_name || !form.illumia_plan_id) {
      alert('Please enter both a plan name and Illumia Plan ID.');
      return;
    }

    setSaving(true);

    if (editingId) {
      const { error } = await supabase
        .from('plans')
        .update(form)
        .eq('id', editingId);

      if (error) {
        alert('Error updating plan: ' + error.message);
        setSaving(false);
        return;
      }
      setSuccessMsg('Plan updated successfully!');
    } else {
      const { error } = await supabase
        .from('plans')
        .insert(form);

      if (error) {
        if (error.message.includes('unique')) {
          alert('A plan with that Illumia Plan ID already exists.');
        } else {
          alert('Error adding plan: ' + error.message);
        }
        setSaving(false);
        return;
      }
      setSuccessMsg('Plan added successfully!');
    }

    setTimeout(() => setSuccessMsg(''), 3000);
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
    setSaving(false);
    fetchPlans();
  };

  const handleCancel = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const filtered = plans.filter((p) =>
    filter === 'All' ? true : p.plan_type === filter
  );

  const fmpCount = plans.filter((p) => p.plan_type === 'fmp').length;
  const athleticsCount = plans.filter((p) => p.plan_type === 'athletics').length;

  if (loading) return (
    <div style={styles.loading}><p style={{ color: GOLD }}>Loading...</p></div>
  );

  return (
    <div style={styles.wrap}>

      {/* TOPBAR */}
      <div style={styles.topbar}>
        <h1 style={styles.topbarTitle}>
          Plans
          <span style={styles.topbarCount}>{plans.length} total</span>
        </h1>
        <button
          style={styles.topbarBtn}
          onClick={() => {
            setShowForm(!showForm);
            setEditingId(null);
            setForm(emptyForm);
          }}
        >
          {showForm && !editingId ? '✕ Cancel' : '+ Add Plan'}
        </button>
      </div>

      <div style={styles.content}>

        {/* SUCCESS */}
        {successMsg && (
          <div style={styles.successBanner}>✅ {successMsg}</div>
        )}

        {/* STAT CARDS */}
        <div style={styles.statGrid}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>FMP Plans</div>
            <div style={styles.statValue}>{fmpCount}</div>
            <div style={styles.statSub}>Flatiron Meal Plan app</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Athletics Plans</div>
            <div style={{ ...styles.statValue, color: GOLD }}>{athleticsCount}</div>
            <div style={styles.statSub}>CU Gold Card app</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Total Plans</div>
            <div style={styles.statValue}>{plans.length}</div>
            <div style={styles.statSub}>Across both apps</div>
          </div>
        </div>

        {/* FORM */}
        {showForm && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>
              {editingId ? 'Edit Plan' : 'Add New Plan'}
            </h2>
            <form onSubmit={handleSubmit}>

              <div style={styles.formGrid}>

                <div style={styles.formRow}>
                  <label style={styles.label}>PLAN NAME *</label>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="e.g. CU Football A"
                    value={form.plan_name}
                    onChange={(e) => setForm({ ...form, plan_name: e.target.value })}
                    required
                  />
                </div>

                <div style={styles.formRow}>
                  <label style={styles.label}>ILLUMIA PLAN ID *</label>
                  <input
                    style={styles.input}
                    type="text"
                    placeholder="e.g. 10042"
                    value={form.illumia_plan_id}
                    onChange={(e) => setForm({ ...form, illumia_plan_id: e.target.value })}
                    required
                  />
                  <div style={styles.fieldHint}>
                    Unique plan ID assigned by Illumia
                  </div>
                </div>

                <div style={styles.formRow}>
                  <label style={styles.label}>APP *</label>
                  <select
                    style={styles.select}
                    value={form.plan_type}
                    onChange={(e) => setForm({ ...form, plan_type: e.target.value })}
                    required
                  >
                    <option value="fmp">Flatiron Meal Plan App</option>
                    <option value="athletics">CU Athletics Gold Card App</option>
                  </select>
                </div>

                <div style={styles.formRow}>
                  <label style={styles.label}>STATUS</label>
                  <label style={styles.checkLabel}>
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      style={{ marginRight: '8px' }}
                    />
                    Active — allow login with this plan
                  </label>
                </div>

              </div>

              <div style={styles.formBtns}>
                <button type="submit" style={styles.submitBtn} disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Plan'}
                </button>
                <button type="button" style={styles.cancelBtn} onClick={handleCancel}>
                  Cancel
                </button>
              </div>

            </form>
          </div>
        )}

        {/* FILTER */}
        <div style={styles.filterRow}>
          {['All', 'fmp', 'athletics'].map((f) => (
            <button
              key={f}
              style={{
                ...styles.filterBtn,
                ...(filter === f ? styles.filterBtnActive : {}),
              }}
              onClick={() => setFilter(f)}
            >
              {f === 'All' ? 'All Plans' :
               f === 'fmp' ? 'FMP App' :
               'Gold Card App'}
            </button>
          ))}
        </div>

        {/* TABLE */}
        <div style={styles.tableWrap}>
          {filtered.length === 0 ? (
            <div style={styles.empty}>
              No plans yet. Click "+ Add Plan" to add one.
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Plan Name', 'Illumia Plan ID', 'App', 'Active', 'Actions'].map((h) => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((plan) => (
                  <tr key={plan.id}>
                    <td style={{ ...styles.td, fontWeight: '500' }}>
                      {plan.plan_name}
                    </td>
                    <td style={{ ...styles.td, fontFamily: 'monospace', color: '#888' }}>
                      {plan.illumia_plan_id}
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        background: plan.plan_type === 'athletics'
                          ? 'rgba(255,194,32,0.15)'
                          : '#f0f0f0',
                        color: plan.plan_type === 'athletics'
                          ? '#b8860b'
                          : '#666',
                      }}>
                        {plan.plan_type === 'athletics' ? 'Gold Card' : 'FMP'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <Toggle
                        value={plan.active}
                        onChange={() => handleToggle(plan)}
                      />
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionBtns}>
                        <button
                          style={styles.editBtn}
                          onClick={() => handleEdit(plan)}
                        >
                          Edit
                        </button>
                        <button
                          style={styles.deleteBtn}
                          onClick={() => handleDelete(plan.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: '36px',
        height: '20px',
        borderRadius: '10px',
        background: value ? GOLD : '#ddd',
        position: 'relative',
        cursor: 'pointer',
        transition: 'background 0.2s',
      }}
    >
      <div style={{
        position: 'absolute',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        background: '#fff',
        top: '3px',
        left: value ? '19px' : '3px',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
  topbar: {
    background: '#fff',
    borderBottom: '1px solid #e0e0e0',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  topbarTitle: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#111',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  topbarCount: {
    fontSize: '13px',
    fontWeight: '400',
    color: '#aaa',
  },
  topbarBtn: {
    background: GOLD,
    color: '#111',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  content: { padding: '24px', flex: 1 },
  successBanner: {
    background: '#f0fff4',
    border: '1px solid #86efac',
    borderRadius: '10px',
    padding: '12px 16px',
    marginBottom: '20px',
    color: '#166534',
    fontSize: '14px',
  },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    padding: '16px 20px',
  },
  statLabel: { color: '#888', fontSize: '12px', marginBottom: '6px' },
  statValue: {
    fontSize: '28px',
    fontWeight: '500',
    color: '#111',
    lineHeight: 1,
  },
  statSub: { color: '#aaa', fontSize: '11px', marginTop: '4px' },
  formCard: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '14px',
    padding: '24px',
    marginBottom: '24px',
  },
  formTitle: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#111',
    marginBottom: '20px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '4px',
  },
  formRow: { marginBottom: '16px' },
  label: {
    display: 'block',
    color: '#888',
    fontSize: '10px',
    letterSpacing: '1px',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#111',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#111',
    fontFamily: 'inherit',
    outline: 'none',
    background: '#fff',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  fieldHint: {
    color: '#bbb',
    fontSize: '11px',
    marginTop: '4px',
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#444',
    cursor: 'pointer',
    marginTop: '8px',
  },
  formBtns: {
    display: 'flex',
    gap: '12px',
  },
  submitBtn: {
    background: GOLD,
    color: '#111',
    border: 'none',
    borderRadius: '10px',
    padding: '11px 24px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  cancelBtn: {
    background: 'none',
    color: '#888',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    padding: '11px 24px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  filterRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  filterBtn: {
    background: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '20px',
    padding: '6px 14px',
    fontSize: '12px',
    color: '#666',
    cursor: 'pointer',
  },
  filterBtnActive: {
    background: GOLD,
    border: `1px solid ${GOLD}`,
    color: '#111',
    fontWeight: '500',
  },
  tableWrap: {
    background: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    background: '#f9f9f9',
    color: '#888',
    fontSize: '11px',
    padding: '10px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #e0e0e0',
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    color: '#111',
    verticalAlign: 'middle',
  },
  badge: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '500',
  },
  actionBtns: {
    display: 'flex',
    gap: '6px',
  },
  editBtn: {
    background: 'none',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    color: '#444',
    cursor: 'pointer',
  },
  deleteBtn: {
    background: 'none',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '12px',
    color: '#ff6b6b',
    cursor: 'pointer',
  },
  empty: {
    padding: '32px',
    textAlign: 'center',
    color: '#888',
    fontSize: '13px',
  },
};
